import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// =============================================================================
// Spanish Translations (Default)
// =============================================================================

const es = {
	translation: {
		// Common
		common: {
			loading: "Cargando...",
			error: "Error",
			save: "Guardar",
			cancel: "Cancelar",
			delete: "Eliminar",
			edit: "Editar",
			create: "Crear",
			search: "Buscar",
			filter: "Filtrar",
			clearFilters: "Limpiar filtros",
			noResults: "Sin resultados",
			actions: "Acciones",
			confirm: "Confirmar",
			close: "Cerrar",
			yes: "Sí",
			no: "No",
			all: "Todos",
			active: "Activo",
			inactive: "Inactivo",
			pending: "Pendiente",
			completed: "Completado",
			export: "Exportar",
			import: "Importar",
			download: "Descargar",
			print: "Imprimir",
			refresh: "Actualizar",
			back: "Volver",
			next: "Siguiente",
			previous: "Anterior",
			of: "de",
			items: "elementos",
			selected: "seleccionado(s)",
			required: "Requerido",
			optional: "Opcional",
		},

		// Navigation
		nav: {
			dashboard: "Dashboard",
			assets: "Activos",
			assetTypes: "Tipos de Activo",
			locations: "Ubicaciones",
			users: "Usuarios",
			settings: "Configuración",
			main: "Principal",
			admin: "Administración",
			logout: "Cerrar sesión",
		},

		// Auth
		auth: {
			login: "Iniciar sesión",
			logout: "Cerrar sesión",
			email: "Correo electrónico",
			password: "Contraseña",
			forgotPassword: "¿Olvidaste tu contraseña?",
			loginButton: "Ingresar",
			loggingIn: "Iniciando sesión...",
			invalidCredentials: "Credenciales inválidas",
			sessionExpired: "Tu sesión ha expirado",
			welcomeBack: "Bienvenido de nuevo",
			enterCredentials: "Ingresa tus credenciales para acceder",
		},

		// Dashboard
		dashboard: {
			title: "Dashboard",
			subtitle: "Resumen general del inventario de activos",
			totalAssets: "Total Activos",
			activeAssets: "Activos Activos",
			inMaintenance: "En Mantenimiento",
			alerts: "Alertas",
			registeredInSystem: "Registrados en el sistema",
			inNormalOperation: "En operación normal",
			inProgress: "en progreso",
			critical: "críticas",
			warnings: "advertencias",
			assetsByType: "Activos por Tipo",
			assetsByLocation: "Activos por Ubicación",
			distributionByCategory: "Distribución por categoría de activo",
			distributionByLocation: "Distribución por sede principal",
			upcomingMaintenances: "Próximos Mantenimientos",
			scheduledNext30Days: "Programados para los próximos 30 días",
			recentActivity: "Actividad Reciente",
			lastMovements: "Últimos movimientos registrados",
			viewAll: "Ver todos",
			noScheduledMaintenances: "No hay mantenimientos programados",
			next30DaysFree: "Los próximos 30 días están libres",
			noRecentActivity: "No hay actividad reciente",
			movementsWillAppear: "Los movimientos aparecerán aquí",
			activeAlerts: "Alertas Activas",
			requiresImmediateAttention: "Requieren atención inmediata",
			viewAllAlerts: "Ver todas las alertas",
			quickActions: "Acciones Rápidas",
			quickActionsDesc: "Accesos directos a funciones frecuentes",
			registerAsset: "Registrar Activo",
			manageLocations: "Gestionar Ubicaciones",
			searchAssets: "Buscar Activos",
			viewAssets: "Ver Activos",
			newAsset: "Nuevo Activo",
			noDataAvailable: "No hay datos disponibles",
		},

		// Assets
		assets: {
			title: "Activos",
			subtitle: "Gestiona el inventario de activos de la organización",
			assetList: "Lista de Activos",
			newAsset: "Nuevo Activo",
			editAsset: "Editar Activo",
			deleteAsset: "Eliminar Activo",
			assetDetail: "Ver Detalle",
			noAssets: "No se encontraron activos",
			noAssetsDesc: "Comienza registrando tu primer activo",
			registered: "registrado",
			registeredPlural: "registrados",
			code: "Código",
			name: "Nombre",
			brand: "Marca",
			model: "Modelo",
			serial: "Serial",
			type: "Tipo",
			location: "Ubicación",
			status: "Estado",
			createdAt: "Fecha de registro",
			updatedAt: "Última actualización",
			notes: "Notas",
			generateQR: "Generar QR",
			exportAssets: "Exportar Activos",
			exportDescription: "Exporta la lista de activos con los filtros aplicados",
			statusActive: "Activo",
			statusInMaintenance: "En Mantenimiento",
			statusInactive: "Inactivo",
			statusDisposed: "Dado de Baja",
			confirmDelete: "¿Estás seguro de que deseas eliminar este activo?",
			confirmDeleteDesc:
				"Esta acción no se puede deshacer. El activo será eliminado permanentemente.",
			baseInfo: "Información Base",
			customFields: "Campos Personalizados",
			overview: "Resumen",
			maintenances: "Mantenimientos",
			movements: "Movimientos",
			history: "Historial",
			lastMaintenance: "Último mantenimiento",
			nextMaintenance: "Próximo mantenimiento",
			noMaintenances: "Sin mantenimientos registrados",
			noMovements: "Sin movimientos registrados",
			noHistory: "Sin historial de cambios",
		},

		// Asset Types
		assetTypes: {
			title: "Tipos de Activo",
			subtitle: "Define los esquemas de campos para cada tipo de activo",
			newAssetType: "Nuevo Tipo",
			editAssetType: "Editar Tipo",
			deleteAssetType: "Eliminar Tipo",
			noAssetTypes: "No hay tipos de activo",
			noAssetTypesDesc: "Crea tu primer tipo de activo para comenzar",
			fieldBuilder: "Constructor de Campos",
			preview: "Vista Previa",
			addField: "Agregar Campo",
			fieldName: "Nombre del campo",
			fieldType: "Tipo de campo",
			fieldRequired: "Requerido",
			fieldHelpText: "Texto de ayuda",
			confirmDelete: "¿Eliminar este tipo de activo?",
			confirmDeleteDesc: "Los activos asociados perderán su referencia al tipo.",
			assetsCount: "activo(s) asociado(s)",
		},

		// Locations
		locations: {
			title: "Ubicaciones",
			subtitle: "Gestiona la estructura jerárquica de ubicaciones",
			newLocation: "Nueva Ubicación",
			editLocation: "Editar Ubicación",
			deleteLocation: "Eliminar Ubicación",
			noLocations: "No hay ubicaciones",
			noLocationsDesc: "Crea tu primera ubicación para comenzar",
			parent: "Ubicación padre",
			noParent: "Sin padre (raíz)",
			locationType: "Tipo de ubicación",
			sede: "Sede",
			piso: "Piso",
			area: "Área",
			espacio: "Espacio",
			assetsInLocation: "Activos en esta ubicación",
			subLocations: "Sub-ubicaciones",
			confirmDelete: "¿Eliminar esta ubicación?",
			confirmDeleteDesc: "Las sub-ubicaciones y activos asociados serán afectados.",
		},

		// Maintenances
		maintenances: {
			title: "Mantenimientos",
			newMaintenance: "Nuevo Mantenimiento",
			editMaintenance: "Editar Mantenimiento",
			typePreventive: "Preventivo",
			typeCorrective: "Correctivo",
			typeCalibration: "Calibración",
			statusScheduled: "Programado",
			statusInProgress: "En Progreso",
			statusCompleted: "Completado",
			statusCancelled: "Cancelado",
			scheduledDate: "Fecha programada",
			completedDate: "Fecha completado",
			technician: "Técnico",
			description: "Descripción",
			observations: "Observaciones",
			cost: "Costo",
		},

		// Movements
		movements: {
			title: "Movimientos",
			newMovement: "Nuevo Movimiento",
			typeTransfer: "Traslado",
			typeLoan: "Préstamo",
			typeReturn: "Devolución",
			typeDisposal: "Baja",
			from: "Origen",
			to: "Destino",
			reason: "Motivo",
			date: "Fecha",
			responsiblePerson: "Responsable",
		},

		// Users
		users: {
			title: "Usuarios",
			subtitle: "Administra los usuarios del sistema",
			newUser: "Nuevo Usuario",
			editUser: "Editar Usuario",
			name: "Nombre",
			email: "Correo electrónico",
			role: "Rol",
			roleAdmin: "Administrador",
			roleOperator: "Operador",
			roleTechnician: "Técnico",
			roleViewer: "Visor",
			status: "Estado",
			statusActive: "Activo",
			statusInactive: "Inactivo",
		},

		// Settings
		settings: {
			title: "Configuración",
			subtitle: "Configura las preferencias del sistema",
			general: "General",
			appearance: "Apariencia",
			notifications: "Notificaciones",
			language: "Idioma",
			theme: "Tema",
			themeLight: "Claro",
			themeDark: "Oscuro",
			themeSystem: "Sistema",
			organization: "Organización",
			organizationDesc: "Configuración de la IPS",
			organizationPlaceholder: "La configuración de organización se implementará más adelante.",
		},

		// Errors
		errors: {
			generic: "Ha ocurrido un error",
			genericDesc: "Por favor, intenta de nuevo más tarde",
			notFound: "Página no encontrada",
			notFoundDesc: "La página que buscas no existe",
			unauthorized: "No autorizado",
			unauthorizedDesc: "No tienes permisos para acceder a esta página",
			networkError: "Error de conexión",
			networkErrorDesc: "Verifica tu conexión a internet",
			serverError: "Error del servidor",
			serverErrorDesc: "El servidor no pudo procesar tu solicitud",
			validationError: "Error de validación",
			retry: "Reintentar",
			goHome: "Ir al inicio",
			goBack: "Volver atrás",
		},

		// Empty states
		empty: {
			noData: "Sin datos",
			noDataDesc: "No hay información para mostrar",
			noResults: "Sin resultados",
			noResultsDesc: "No se encontraron coincidencias con tu búsqueda",
			createFirst: "Crear el primero",
		},

		// Toast messages
		toast: {
			createSuccess: "Creado exitosamente",
			updateSuccess: "Actualizado exitosamente",
			deleteSuccess: "Eliminado exitosamente",
			saveSuccess: "Guardado exitosamente",
			error: "Ha ocurrido un error",
			errorTryAgain: "Por favor, intenta de nuevo",
			copied: "Copiado al portapapeles",
			exported: "Exportación completada",
			imported: "Importación completada",
		},

		// Time
		time: {
			today: "Hoy",
			tomorrow: "Mañana",
			yesterday: "Ayer",
			inDays: "En {{count}} días",
			inWeeks: "En {{count}} semanas",
			ago: "Hace {{time}}",
			minutes: "min",
			hours: "h",
			days: "días",
		},

		// Keyboard shortcuts
		shortcuts: {
			title: "Atajos de Teclado",
			general: "General",
			navigation: "Navegación",
			actions: "Acciones",
			search: "Buscar",
			newItem: "Nuevo elemento",
			save: "Guardar",
			cancel: "Cancelar/Cerrar",
			help: "Mostrar atajos",
			goToDashboard: "Ir al Dashboard",
			goToAssets: "Ir a Activos",
			goToLocations: "Ir a Ubicaciones",
			toggleSidebar: "Alternar barra lateral",
		},
	},
};

// =============================================================================
// English Translations
// =============================================================================

const en = {
	translation: {
		// Common
		common: {
			loading: "Loading...",
			error: "Error",
			save: "Save",
			cancel: "Cancel",
			delete: "Delete",
			edit: "Edit",
			create: "Create",
			search: "Search",
			filter: "Filter",
			clearFilters: "Clear filters",
			noResults: "No results",
			actions: "Actions",
			confirm: "Confirm",
			close: "Close",
			yes: "Yes",
			no: "No",
			all: "All",
			active: "Active",
			inactive: "Inactive",
			pending: "Pending",
			completed: "Completed",
			export: "Export",
			import: "Import",
			download: "Download",
			print: "Print",
			refresh: "Refresh",
			back: "Back",
			next: "Next",
			previous: "Previous",
			of: "of",
			items: "items",
			selected: "selected",
			required: "Required",
			optional: "Optional",
		},

		// Navigation
		nav: {
			dashboard: "Dashboard",
			assets: "Assets",
			assetTypes: "Asset Types",
			locations: "Locations",
			users: "Users",
			settings: "Settings",
			main: "Main",
			admin: "Administration",
			logout: "Log out",
		},

		// Auth
		auth: {
			login: "Log in",
			logout: "Log out",
			email: "Email",
			password: "Password",
			forgotPassword: "Forgot your password?",
			loginButton: "Sign in",
			loggingIn: "Signing in...",
			invalidCredentials: "Invalid credentials",
			sessionExpired: "Your session has expired",
			welcomeBack: "Welcome back",
			enterCredentials: "Enter your credentials to access",
		},

		// Dashboard
		dashboard: {
			title: "Dashboard",
			subtitle: "General overview of asset inventory",
			totalAssets: "Total Assets",
			activeAssets: "Active Assets",
			inMaintenance: "In Maintenance",
			alerts: "Alerts",
			registeredInSystem: "Registered in system",
			inNormalOperation: "In normal operation",
			inProgress: "in progress",
			critical: "critical",
			warnings: "warnings",
			assetsByType: "Assets by Type",
			assetsByLocation: "Assets by Location",
			distributionByCategory: "Distribution by asset category",
			distributionByLocation: "Distribution by main location",
			upcomingMaintenances: "Upcoming Maintenances",
			scheduledNext30Days: "Scheduled for the next 30 days",
			recentActivity: "Recent Activity",
			lastMovements: "Last recorded movements",
			viewAll: "View all",
			noScheduledMaintenances: "No scheduled maintenances",
			next30DaysFree: "The next 30 days are free",
			noRecentActivity: "No recent activity",
			movementsWillAppear: "Movements will appear here",
			activeAlerts: "Active Alerts",
			requiresImmediateAttention: "Require immediate attention",
			viewAllAlerts: "View all alerts",
			quickActions: "Quick Actions",
			quickActionsDesc: "Shortcuts to common functions",
			registerAsset: "Register Asset",
			manageLocations: "Manage Locations",
			searchAssets: "Search Assets",
			viewAssets: "View Assets",
			newAsset: "New Asset",
			noDataAvailable: "No data available",
		},

		// Assets
		assets: {
			title: "Assets",
			subtitle: "Manage the organization's asset inventory",
			assetList: "Asset List",
			newAsset: "New Asset",
			editAsset: "Edit Asset",
			deleteAsset: "Delete Asset",
			assetDetail: "Asset Detail",
			noAssets: "No assets found",
			noAssetsDesc: "Start by registering your first asset",
			registered: "registered",
			registeredPlural: "registered",
			code: "Code",
			name: "Name",
			brand: "Brand",
			model: "Model",
			serial: "Serial",
			type: "Type",
			location: "Location",
			status: "Status",
			createdAt: "Registration date",
			updatedAt: "Last update",
			notes: "Notes",
			generateQR: "Generate QR",
			exportAssets: "Export Assets",
			exportDescription: "Export the asset list with applied filters",
			statusActive: "Active",
			statusInMaintenance: "In Maintenance",
			statusInactive: "Inactive",
			statusDisposed: "Disposed",
			confirmDelete: "Are you sure you want to delete this asset?",
			confirmDeleteDesc: "This action cannot be undone. The asset will be permanently deleted.",
			baseInfo: "Base Information",
			customFields: "Custom Fields",
			overview: "Overview",
			maintenances: "Maintenances",
			movements: "Movements",
			history: "History",
			lastMaintenance: "Last maintenance",
			nextMaintenance: "Next maintenance",
			noMaintenances: "No maintenances recorded",
			noMovements: "No movements recorded",
			noHistory: "No change history",
		},

		// Asset Types
		assetTypes: {
			title: "Asset Types",
			subtitle: "Define field schemas for each asset type",
			newAssetType: "New Type",
			editAssetType: "Edit Type",
			deleteAssetType: "Delete Type",
			noAssetTypes: "No asset types",
			noAssetTypesDesc: "Create your first asset type to get started",
			fieldBuilder: "Field Builder",
			preview: "Preview",
			addField: "Add Field",
			fieldName: "Field name",
			fieldType: "Field type",
			fieldRequired: "Required",
			fieldHelpText: "Help text",
			confirmDelete: "Delete this asset type?",
			confirmDeleteDesc: "Associated assets will lose their type reference.",
			assetsCount: "associated asset(s)",
		},

		// Locations
		locations: {
			title: "Locations",
			subtitle: "Manage the hierarchical structure of locations",
			newLocation: "New Location",
			editLocation: "Edit Location",
			deleteLocation: "Delete Location",
			noLocations: "No locations",
			noLocationsDesc: "Create your first location to get started",
			parent: "Parent location",
			noParent: "No parent (root)",
			locationType: "Location type",
			sede: "Headquarters",
			piso: "Floor",
			area: "Area",
			espacio: "Space",
			assetsInLocation: "Assets in this location",
			subLocations: "Sub-locations",
			confirmDelete: "Delete this location?",
			confirmDeleteDesc: "Sub-locations and associated assets will be affected.",
		},

		// Maintenances
		maintenances: {
			title: "Maintenances",
			newMaintenance: "New Maintenance",
			editMaintenance: "Edit Maintenance",
			typePreventive: "Preventive",
			typeCorrective: "Corrective",
			typeCalibration: "Calibration",
			statusScheduled: "Scheduled",
			statusInProgress: "In Progress",
			statusCompleted: "Completed",
			statusCancelled: "Cancelled",
			scheduledDate: "Scheduled date",
			completedDate: "Completed date",
			technician: "Technician",
			description: "Description",
			observations: "Observations",
			cost: "Cost",
		},

		// Movements
		movements: {
			title: "Movements",
			newMovement: "New Movement",
			typeTransfer: "Transfer",
			typeLoan: "Loan",
			typeReturn: "Return",
			typeDisposal: "Disposal",
			from: "From",
			to: "To",
			reason: "Reason",
			date: "Date",
			responsiblePerson: "Responsible person",
		},

		// Users
		users: {
			title: "Users",
			subtitle: "Manage system users",
			newUser: "New User",
			editUser: "Edit User",
			name: "Name",
			email: "Email",
			role: "Role",
			roleAdmin: "Administrator",
			roleOperator: "Operator",
			roleTechnician: "Technician",
			roleViewer: "Viewer",
			status: "Status",
			statusActive: "Active",
			statusInactive: "Inactive",
		},

		// Settings
		settings: {
			title: "Settings",
			subtitle: "Configure system preferences",
			general: "General",
			appearance: "Appearance",
			notifications: "Notifications",
			language: "Language",
			theme: "Theme",
			themeLight: "Light",
			themeDark: "Dark",
			themeSystem: "System",
			organization: "Organization",
			organizationDesc: "IPS configuration",
			organizationPlaceholder: "Organization settings will be implemented later.",
		},

		// Errors
		errors: {
			generic: "An error has occurred",
			genericDesc: "Please try again later",
			notFound: "Page not found",
			notFoundDesc: "The page you're looking for doesn't exist",
			unauthorized: "Unauthorized",
			unauthorizedDesc: "You don't have permission to access this page",
			networkError: "Connection error",
			networkErrorDesc: "Check your internet connection",
			serverError: "Server error",
			serverErrorDesc: "The server couldn't process your request",
			validationError: "Validation error",
			retry: "Retry",
			goHome: "Go home",
			goBack: "Go back",
		},

		// Empty states
		empty: {
			noData: "No data",
			noDataDesc: "There's no information to display",
			noResults: "No results",
			noResultsDesc: "No matches found for your search",
			createFirst: "Create first",
		},

		// Toast messages
		toast: {
			createSuccess: "Created successfully",
			updateSuccess: "Updated successfully",
			deleteSuccess: "Deleted successfully",
			saveSuccess: "Saved successfully",
			error: "An error has occurred",
			errorTryAgain: "Please try again",
			copied: "Copied to clipboard",
			exported: "Export completed",
			imported: "Import completed",
		},

		// Time
		time: {
			today: "Today",
			tomorrow: "Tomorrow",
			yesterday: "Yesterday",
			inDays: "In {{count}} days",
			inWeeks: "In {{count}} weeks",
			ago: "{{time}} ago",
			minutes: "min",
			hours: "h",
			days: "days",
		},

		// Keyboard shortcuts
		shortcuts: {
			title: "Keyboard Shortcuts",
			general: "General",
			navigation: "Navigation",
			actions: "Actions",
			search: "Search",
			newItem: "New item",
			save: "Save",
			cancel: "Cancel/Close",
			help: "Show shortcuts",
			goToDashboard: "Go to Dashboard",
			goToAssets: "Go to Assets",
			goToLocations: "Go to Locations",
			toggleSidebar: "Toggle sidebar",
		},
	},
};

// =============================================================================
// i18n Configuration
// =============================================================================

i18n
	.use(LanguageDetector)
	.use(initReactI18next)
	.init({
		resources: {
			es,
			en,
		},
		fallbackLng: "es",
		defaultNS: "translation",
		interpolation: {
			escapeValue: false,
		},
		detection: {
			order: ["localStorage", "navigator"],
			caches: ["localStorage"],
		},
	});

export default i18n;
